import { socketManager, RouletteResult } from '../../socket/SocketManager';

const RED_NUMBERS = [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36];
const BLACK_NUMBERS = [2, 4, 6, 8, 10, 11, 13, 15, 17, 20, 22, 24, 26, 28, 29, 31, 33, 35];

// European roulette wheel order
const WHEEL_ORDER = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36,
  11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9,
  22, 18, 29, 7, 28, 12, 35, 3, 26,
];

const CHIP_DENOMINATIONS = [10, 25, 50, 100, 500];

const CHIP_COLORS: Record<number, number> = {
  10: 0xcc0000,
  25: 0x00aa00,
  50: 0x0044cc,
  100: 0x222222,
  500: 0x7b2fbe,
};

const GOLD = 0xffd700;

interface PendingBet {
  type: string;
  numbers: number[];
  amount: number;
  useDirty: boolean;
  label: string;
}

/** Aggregated bets per cell key for chip rendering */
interface CellBetInfo {
  totalAmount: number;
  highestDenom: number;
}

export class RouletteGame {
  private betAmount = 50;
  private useDirty = false;
  private pendingBets: PendingBet[] = [];
  private elements: Phaser.GameObjects.GameObject[] = [];
  private tweens: Phaser.Tweens.Tween[] = [];
  private isSpinning = false;
  private balanceClean = 0;
  private balanceDirty = 0;
  private balanceText: Phaser.GameObjects.Text | null = null;
  private pendingBetsText: Phaser.GameObjects.Text | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;
  private wheelContainer: Phaser.GameObjects.Container | null = null;
  private ballGraphic: Phaser.GameObjects.Graphics | null = null;
  private winningMarker: Phaser.GameObjects.Text | null = null;
  private tableCells: Map<string, { x: number; y: number; w: number; h: number }> = new Map();
  private tournerBtn: Phaser.GameObjects.Container | null = null;
  private toggleContainer: Phaser.GameObjects.Container | null = null;
  private chipSelectors: Phaser.GameObjects.Container[] = [];
  private chipContainers: Phaser.GameObjects.Container[] = [];
  private timers: Phaser.Time.TimerEvent[] = [];
  private showingResults = false;

  // Cached layout values for animation
  private wheelCenterX = 0;
  private wheelCenterY = 0;
  private wheelRadius = 0;

  // Cached chip selector position for refresh
  private chipSelectorCenterX = 0;
  private chipSelectorY = 0;

  constructor(
    private scene: Phaser.Scene,
    private graphics: Phaser.GameObjects.Graphics,
    private building: any,
    private stateData: any,
    private onBack: () => void,
  ) {
    try {
      const gameScene = this.scene.scene.get('GameScene') as any;
      if (gameScene) {
        this.balanceDirty = gameScene.moneyDirty ?? 500;
        this.balanceClean = gameScene.moneyClean ?? 0;
      }
    } catch {
      // fallback
    }
  }

  render(): void {
    // Disable right-click context menu for right-click chip removal
    this.scene.input.mouse?.disableContextMenu();

    this.renderWheel();
    this.renderTable();
    this.renderControls();
  }

  /* ===================================================================
   *  WHEEL -- Small, top-center above the table
   * =================================================================== */

  private renderWheel(): void {
    const { width: W, height: H } = this.scene.scale;
    const cx = W / 2;

    const radius = Math.min(W * 0.06, 60);
    const wheelCenterX = cx;
    const wheelCenterY = H * 0.1;

    // Cache for animation
    this.wheelCenterX = wheelCenterX;
    this.wheelCenterY = wheelCenterY;
    this.wheelRadius = radius;

    // Wheel background panel
    const panelW = radius * 2 + 30;
    const panelH = radius * 2 + 50;
    this.graphics.fillStyle(0x1a1a2e, 0.9);
    this.graphics.fillRoundedRect(wheelCenterX - panelW / 2, wheelCenterY - radius - 15, panelW, panelH, 12);
    this.graphics.lineStyle(2, 0xe6b800, 0.6);
    this.graphics.strokeRoundedRect(wheelCenterX - panelW / 2, wheelCenterY - radius - 15, panelW, panelH, 12);

    // Create wheel container for rotation
    this.wheelContainer = this.scene.add.container(wheelCenterX, wheelCenterY).setDepth(101);
    this.elements.push(this.wheelContainer);

    // Draw wheel segments
    const segmentAngle = (Math.PI * 2) / WHEEL_ORDER.length;
    const wheelGraphics = this.scene.add.graphics().setDepth(101);

    // Outer ring (gold)
    wheelGraphics.lineStyle(3, 0xe6b800, 0.9);
    wheelGraphics.strokeCircle(0, 0, radius + 2);

    const labelFontSize = Math.max(7, Math.round(radius * 0.1)) + 'px';

    for (let i = 0; i < WHEEL_ORDER.length; i++) {
      const num = WHEEL_ORDER[i];
      const startAngle = i * segmentAngle - Math.PI / 2;
      const endAngle = startAngle + segmentAngle;

      let color: number;
      if (num === 0) {
        color = 0x00aa00;
      } else if (RED_NUMBERS.includes(num)) {
        color = 0xcc2222;
      } else {
        color = 0x1a1a1a;
      }

      wheelGraphics.fillStyle(color, 1);
      wheelGraphics.slice(0, 0, radius, startAngle, endAngle, false);
      wheelGraphics.fillPath();

      // Segment divider
      wheelGraphics.lineStyle(1, 0xe6b800, 0.4);
      const sx = Math.cos(startAngle) * radius;
      const sy = Math.sin(startAngle) * radius;
      wheelGraphics.lineBetween(0, 0, sx, sy);

      // Number label on wheel
      const labelAngle = startAngle + segmentAngle / 2;
      const labelR = radius * 0.75;
      const lx = Math.cos(labelAngle) * labelR;
      const ly = Math.sin(labelAngle) * labelR;
      const numText = this.scene.add.text(lx, ly, `${num}`, {
        fontSize: labelFontSize,
        fontFamily: 'monospace',
        color: '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(102);
      this.wheelContainer.add(numText);
    }

    // Inner decorative rings
    wheelGraphics.lineStyle(2, 0xe6b800, 0.7);
    wheelGraphics.strokeCircle(0, 0, radius * 0.55);
    wheelGraphics.fillStyle(0x2a1a0a, 1);
    wheelGraphics.fillCircle(0, 0, radius * 0.5);
    wheelGraphics.lineStyle(1, 0xe6b800, 0.5);
    wheelGraphics.strokeCircle(0, 0, radius * 0.3);

    // Center hub
    wheelGraphics.fillStyle(0xe6b800, 1);
    wheelGraphics.fillCircle(0, 0, Math.max(4, radius * 0.08));
    wheelGraphics.fillStyle(0x2a1a0a, 1);
    wheelGraphics.fillCircle(0, 0, Math.max(2, radius * 0.04));

    this.wheelContainer.add(wheelGraphics);

    // Ball (outside container so it can orbit independently)
    this.ballGraphic = this.scene.add.graphics().setDepth(104);
    this.ballGraphic.fillStyle(0xffffff, 1);
    const ballR = Math.max(3, radius * 0.06);
    this.ballGraphic.fillCircle(wheelCenterX, wheelCenterY - radius + ballR * 2, ballR);
    this.ballGraphic.lineStyle(1, 0xcccccc, 0.8);
    this.ballGraphic.strokeCircle(wheelCenterX, wheelCenterY - radius + ballR * 2, ballR);
    this.ballGraphic.setAlpha(0); // Hidden until spin
    this.elements.push(this.ballGraphic);

    // Pointer (triangle at top, outside container)
    const pointerG = this.scene.add.graphics().setDepth(105);
    pointerG.fillStyle(0xe6b800, 1);
    pointerG.beginPath();
    pointerG.moveTo(wheelCenterX, wheelCenterY - radius - 4);
    pointerG.lineTo(wheelCenterX - 5, wheelCenterY - radius - 14);
    pointerG.lineTo(wheelCenterX + 5, wheelCenterY - radius - 14);
    pointerG.closePath();
    pointerG.fillPath();
    this.elements.push(pointerG);

    // Winning number display
    this.winningMarker = this.scene.add.text(wheelCenterX, wheelCenterY + radius + 14, '', {
      fontSize: Math.max(10, Math.round(radius * 0.15)) + 'px',
      fontFamily: 'monospace',
      color: '#e6b800',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(101);
    this.elements.push(this.winningMarker);
  }

  /* ===================================================================
   *  BETTING TABLE -- Centered below wheel
   * =================================================================== */

  private renderTable(): void {
    const { width: W, height: H } = this.scene.scale;
    const cx = W / 2;

    // Total table width = 82% of screen
    const totalTableW = W * 0.82;
    // 14 units: 0.6 zero + 12 numbers + 0.5 for 2:1
    const cellW = totalTableW / 14;
    const cellH = Math.min(cellW * 0.7, H * 0.08);
    const zeroW = cellW * 0.6;
    const colBetW = cellW * 0.5;
    const numCols = 12;
    const gridW = numCols * cellW;

    const tableStartX = cx - totalTableW / 2;
    const tableStartY = H * 0.2;

    const gridH = 3 * cellH;
    const dozenRowH = Math.floor(cellH * 0.6);
    const outsideRowH = Math.floor(cellH * 0.55);

    // Table felt background (dark green)
    const feltPadding = Math.min(10, W * 0.01);
    const feltX = tableStartX - feltPadding;
    const feltY = tableStartY - feltPadding;
    const feltW = totalTableW + feltPadding * 2;
    const feltH = gridH + dozenRowH + outsideRowH + feltPadding * 2 + 4;

    // Wood border
    this.graphics.fillStyle(0x2a1a0a, 1);
    this.graphics.fillRoundedRect(feltX - 6, feltY - 6, feltW + 12, feltH + 12, 10);
    // Felt
    this.graphics.fillStyle(0x0a2a0a, 1);
    this.graphics.fillRoundedRect(feltX, feltY, feltW, feltH, 8);
    // Gold border
    this.graphics.lineStyle(2, 0xccaa00, 0.7);
    this.graphics.strokeRoundedRect(feltX, feltY, feltW, feltH, 8);

    // -- Zero cell (green, spans all 3 rows on the LEFT) --
    const zeroX = tableStartX;
    const zeroY = tableStartY;

    this.graphics.fillStyle(0x00aa00, 1);
    this.graphics.fillRoundedRect(zeroX, zeroY, zeroW, gridH, { tl: 4, tr: 0, bl: 4, br: 0 });
    this.graphics.lineStyle(1, 0xccaa00, 0.5);
    this.graphics.strokeRoundedRect(zeroX, zeroY, zeroW, gridH, { tl: 4, tr: 0, bl: 4, br: 0 });

    const zeroFontSize = Math.max(12, Math.round(cellW * 0.35)) + 'px';
    const zeroText = this.scene.add.text(zeroX + zeroW / 2, zeroY + gridH / 2, '0', {
      fontSize: zeroFontSize,
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(101);
    this.elements.push(zeroText);

    const zeroHit = this.scene.add.zone(zeroX + zeroW / 2, zeroY + gridH / 2, zeroW, gridH)
      .setInteractive({ useHandCursor: true }).setDepth(102);
    zeroHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.rightButtonDown()) {
        this.removeBet('STRAIGHT', [0], '0');
      } else {
        this.placeBet('STRAIGHT', [0], '0');
      }
    });
    zeroHit.on('pointerover', () => zeroText.setAlpha(0.7));
    zeroHit.on('pointerout', () => zeroText.setAlpha(1));
    this.elements.push(zeroHit);
    this.tableCells.set('num_0', { x: zeroX, y: zeroY, w: zeroW, h: gridH });

    // -- Number grid: 3 rows x 12 columns --
    const gridStartX = tableStartX + zeroW;
    const numFontSize = Math.max(9, Math.round(cellW * 0.25)) + 'px';

    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 12; col++) {
        const num = col * 3 + (3 - row);
        const cellX = gridStartX + col * cellW;
        const cy = tableStartY + row * cellH;

        let bgColor: number;
        if (RED_NUMBERS.includes(num)) {
          bgColor = 0xcc2222;
        } else {
          bgColor = 0x1a1a1a;
        }

        // Draw cell background
        this.graphics.fillStyle(bgColor, 1);
        this.graphics.fillRect(cellX, cy, cellW, cellH);
        this.graphics.lineStyle(1, 0xccaa00, 0.3);
        this.graphics.strokeRect(cellX, cy, cellW, cellH);

        // Number text
        const numText = this.scene.add.text(cellX + cellW / 2, cy + cellH / 2, `${num}`, {
          fontSize: numFontSize,
          fontFamily: 'monospace',
          color: '#ffffff',
          fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(101);
        this.elements.push(numText);

        // Hit zone
        const hitZone = this.scene.add.zone(cellX + cellW / 2, cy + cellH / 2, cellW, cellH)
          .setInteractive({ useHandCursor: true }).setDepth(102);
        hitZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
          if (pointer.rightButtonDown()) {
            this.removeBet('STRAIGHT', [num], `${num}`);
          } else {
            this.placeBet('STRAIGHT', [num], `${num}`);
          }
        });

        // Hover effect
        const hoverG = this.scene.add.graphics().setDepth(103).setAlpha(0);
        this.elements.push(hoverG);

        hitZone.on('pointerover', () => {
          hoverG.clear();
          hoverG.lineStyle(2, 0xffffff, 1);
          hoverG.strokeRect(cellX + 1, cy + 1, cellW - 2, cellH - 2);
          hoverG.setAlpha(1);
        });
        hitZone.on('pointerout', () => hoverG.setAlpha(0));

        this.elements.push(hitZone);
        this.tableCells.set(`num_${num}`, { x: cellX, y: cy, w: cellW, h: cellH });
      }
    }

    // -- Column bets (2:1) on the RIGHT side of each row --
    const colBetX = gridStartX + 12 * cellW;
    const colNums = [3, 2, 1]; // top to bottom corresponds to column 3, 2, 1
    const colFontSize = Math.max(8, Math.round(cellW * 0.2)) + 'px';

    for (let row = 0; row < 3; row++) {
      const cbX = colBetX;
      const cbY = tableStartY + row * cellH;

      this.graphics.fillStyle(0x333333, 1);
      this.graphics.fillRect(cbX, cbY, colBetW, cellH);
      this.graphics.lineStyle(1, 0xccaa00, 0.3);
      this.graphics.strokeRect(cbX, cbY, colBetW, cellH);

      const colText = this.scene.add.text(cbX + colBetW / 2, cbY + cellH / 2, '2:1', {
        fontSize: colFontSize,
        fontFamily: 'monospace',
        color: '#e6b800',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(101);
      this.elements.push(colText);

      const colHit = this.scene.add.zone(cbX + colBetW / 2, cbY + cellH / 2, colBetW, cellH)
        .setInteractive({ useHandCursor: true }).setDepth(102);
      const colNum = colNums[row];
      colHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (pointer.rightButtonDown()) {
          this.removeBet('COLUMN', [colNum], `Col ${colNum}`);
        } else {
          this.placeBet('COLUMN', [colNum], `Col ${colNum}`);
        }
      });
      this.elements.push(colHit);
      this.tableCells.set(`col_${colNum}`, { x: cbX, y: cbY, w: colBetW, h: cellH });
    }

    // -- Dozen bets below the number grid --
    const dozenY = tableStartY + gridH + 2;
    const dozenLabels = ['1st 12', '2nd 12', '3rd 12'];
    const dozenW = Math.floor(gridW / 3);
    const dozenFontSize = Math.max(8, Math.round(cellW * 0.2)) + 'px';

    for (let i = 0; i < 3; i++) {
      const dx = gridStartX + i * dozenW;
      const dw = i < 2 ? dozenW : gridW - 2 * dozenW; // last one gets remainder

      this.graphics.fillStyle(0x333333, 1);
      this.graphics.fillRect(dx, dozenY, dw, dozenRowH);
      this.graphics.lineStyle(1, 0xccaa00, 0.3);
      this.graphics.strokeRect(dx, dozenY, dw, dozenRowH);

      const dText = this.scene.add.text(dx + dw / 2, dozenY + dozenRowH / 2, dozenLabels[i], {
        fontSize: dozenFontSize,
        fontFamily: 'monospace',
        color: '#e6b800',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(101);
      this.elements.push(dText);

      const dHit = this.scene.add.zone(dx + dw / 2, dozenY + dozenRowH / 2, dw, dozenRowH)
        .setInteractive({ useHandCursor: true }).setDepth(102);
      const dozenNum = i + 1;
      dHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (pointer.rightButtonDown()) {
          this.removeBet('DOZEN', [dozenNum], dozenLabels[i]);
        } else {
          this.placeBet('DOZEN', [dozenNum], dozenLabels[i]);
        }
      });
      this.elements.push(dHit);
      this.tableCells.set(`dozen_${dozenNum}`, { x: dx, y: dozenY, w: dw, h: dozenRowH });
    }

    // -- Outside bets below dozens (1-18, PAIR, ROUGE, NOIR, IMPAIR, 19-36) --
    const outsideY = dozenY + dozenRowH + 2;
    const outsideBets = [
      { label: '1-18', type: 'LOW', bg: 0x333333 },
      { label: 'PAIR', type: 'EVEN', bg: 0x333333 },
      { label: 'ROUGE', type: 'RED', bg: 0xcc2222 },
      { label: 'NOIR', type: 'BLACK', bg: 0x1a1a1a },
      { label: 'IMPAIR', type: 'ODD', bg: 0x333333 },
      { label: '19-36', type: 'HIGH', bg: 0x333333 },
    ];

    const outsideCellW = Math.floor(gridW / 6);
    const outsideFontSize = Math.max(7, Math.round(cellW * 0.18)) + 'px';
    for (let i = 0; i < outsideBets.length; i++) {
      const bet = outsideBets[i];
      const ox = gridStartX + i * outsideCellW;
      const ow = i < 5 ? outsideCellW : gridW - 5 * outsideCellW; // last gets remainder

      this.graphics.fillStyle(bet.bg, 1);
      this.graphics.fillRect(ox, outsideY, ow, outsideRowH);
      this.graphics.lineStyle(1, 0xccaa00, 0.3);
      this.graphics.strokeRect(ox, outsideY, ow, outsideRowH);

      // Diamond marker for ROUGE
      const labelColor = (bet.type === 'RED') ? '#ffffff' : (bet.type === 'BLACK' ? '#ffffff' : '#e6b800');
      const oText = this.scene.add.text(ox + ow / 2, outsideY + outsideRowH / 2, bet.label, {
        fontSize: outsideFontSize,
        fontFamily: 'monospace',
        color: labelColor,
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(101);
      this.elements.push(oText);

      const oHit = this.scene.add.zone(ox + ow / 2, outsideY + outsideRowH / 2, ow, outsideRowH)
        .setInteractive({ useHandCursor: true }).setDepth(102);
      oHit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (pointer.rightButtonDown()) {
          this.removeBet(bet.type, [], bet.label);
        } else {
          this.placeBet(bet.type, [], bet.label);
        }
      });
      this.elements.push(oHit);

      this.tableCells.set(`outside_${bet.type}`, { x: ox, y: outsideY, w: ow, h: outsideRowH });
    }
  }

  /* ===================================================================
   *  CONTROLS -- Chip selector, toggle, spin button, balance
   *  Positioned below the table, centered
   * =================================================================== */

  private renderControls(): void {
    const { width: W, height: H } = this.scene.scale;
    const cx = W / 2;

    // Calculate table bottom position from responsive layout
    const totalTableW = W * 0.82;
    const cellW = totalTableW / 14;
    const cellH = Math.min(cellW * 0.7, H * 0.08);
    const gridH = 3 * cellH;
    const dozenRowH = Math.floor(cellH * 0.6);
    const outsideRowH = Math.floor(cellH * 0.55);
    const tableStartY = H * 0.2;
    const tableBottomY = tableStartY + gridH + dozenRowH + outsideRowH + 10;

    const controlsY = Math.max(tableBottomY + H * 0.02, H * 0.55);

    // Cache chip selector position for refreshing
    this.chipSelectorCenterX = cx;
    this.chipSelectorY = controlsY;

    // Chip selector bar
    this.renderChipSelector(cx, controlsY);

    // Propre / Sale toggle
    this.renderToggle(cx - W * 0.1, controlsY + H * 0.05);

    // TOURNER button
    this.renderTournerButton(cx + W * 0.07, controlsY + H * 0.05);

    // EFFACER (clear) button
    this.renderClearButton(cx + W * 0.19, controlsY + H * 0.05);

    // Pending bets display
    this.pendingBetsText = this.scene.add.text(W * 0.04, controlsY + H * 0.09, 'Mises: aucune', {
      fontSize: Math.max(10, Math.round(W * 0.012)) + 'px',
      fontFamily: 'monospace',
      color: '#999999',
      wordWrap: { width: W * 0.92 },
    }).setDepth(100);
    this.elements.push(this.pendingBetsText);

    // Balance display
    this.balanceText = this.scene.add.text(cx, controlsY + H * 0.12, this.getBalanceLabel(), {
      fontSize: Math.max(10, Math.round(W * 0.013)) + 'px',
      fontFamily: 'monospace',
      color: '#cccccc',
    }).setOrigin(0.5).setDepth(100);
    this.elements.push(this.balanceText);

    // Result text
    this.resultText = this.scene.add.text(cx, controlsY + H * 0.15, '', {
      fontSize: Math.max(14, Math.round(W * 0.018)) + 'px',
      fontFamily: 'monospace',
      color: '#e6b800',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(102).setAlpha(0);
    this.elements.push(this.resultText);
  }

  private renderChipSelector(centerX: number, y: number): void {
    const { width: W } = this.scene.scale;
    const chipSpacing = Math.min(42, W * 0.045);
    const totalChipsW = (CHIP_DENOMINATIONS.length - 1) * chipSpacing;

    const label = this.scene.add.text(centerX - totalChipsW / 2 - W * 0.05, y, 'Mise:', {
      fontSize: Math.max(11, Math.round(W * 0.014)) + 'px',
      fontFamily: 'monospace',
      color: '#aaaaaa',
    }).setOrigin(0, 0.5).setDepth(100);
    this.elements.push(label);

    const startX = centerX - totalChipsW / 2;
    const chipRadius = Math.min(16, W * 0.018);
    const currentBalance = this.useDirty ? this.balanceDirty : this.balanceClean;

    CHIP_DENOMINATIONS.forEach((amount, i) => {
      const chipX = startX + i * chipSpacing;
      const container = this.scene.add.container(chipX, y).setDepth(109);

      const color = CHIP_COLORS[amount] || 0x888888;
      const isSelected = this.betAmount === amount;
      const isAffordable = amount <= currentBalance;

      const g = this.scene.add.graphics();

      if (isSelected) {
        g.lineStyle(3, 0xffdd44, 0.9);
        g.strokeCircle(0, 0, chipRadius + 3);
      }

      // Chip body -- grey out if not affordable
      if (isAffordable) {
        g.fillStyle(color, 1);
      } else {
        g.fillStyle(0x555555, 0.6);
      }
      g.fillCircle(0, 0, chipRadius);
      g.lineStyle(2, 0xffffff, isAffordable ? 0.9 : 0.3);
      g.strokeCircle(0, 0, chipRadius);
      g.lineStyle(1, 0xffffff, isAffordable ? 0.35 : 0.15);
      g.strokeCircle(0, 0, chipRadius - 4);
      container.add(g);

      const txt = this.scene.add.text(0, 0, `${amount}`, {
        fontSize: amount >= 100 ? '9px' : '10px',
        fontFamily: 'monospace',
        color: isAffordable ? '#ffffff' : '#888888',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      container.add(txt);

      const hitArea = this.scene.add.zone(0, 0, chipRadius * 2 + 6, chipRadius * 2 + 6)
        .setInteractive({ useHandCursor: true });
      container.add(hitArea);

      hitArea.on('pointerdown', () => {
        if (this.isSpinning) return;
        this.betAmount = amount;
        this.refreshChipSelector();
      });
      hitArea.on('pointerover', () => container.setScale(1.1));
      hitArea.on('pointerout', () => container.setScale(1.0));

      this.chipSelectors.push(container);
      this.elements.push(container);
    });
  }

  private refreshChipSelector(): void {
    for (const c of this.chipSelectors) {
      try { c.destroy(); } catch { /* ok */ }
    }
    this.chipSelectors = [];
    this.renderChipSelector(this.chipSelectorCenterX, this.chipSelectorY);
  }

  private renderToggle(x: number, y: number): void {
    this.toggleContainer = this.scene.add.container(x, y).setDepth(100);

    const g = this.scene.add.graphics();
    const bgColor = this.useDirty ? 0x442222 : 0x224422;
    g.fillStyle(bgColor, 1);
    g.fillRoundedRect(-40, -14, 80, 28, 14);
    g.lineStyle(2, this.useDirty ? 0xff4444 : 0x44ff44, 0.8);
    g.strokeRoundedRect(-40, -14, 80, 28, 14);
    this.toggleContainer.add(g);

    const label = this.scene.add.text(0, 0, this.getToggleLabel(), {
      fontSize: '12px',
      fontFamily: 'monospace',
      color: this.useDirty ? '#ff4444' : '#44ff44',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.toggleContainer.add(label);

    const hitZone = this.scene.add.zone(0, 0, 80, 28)
      .setInteractive({ useHandCursor: true });
    this.toggleContainer.add(hitZone);

    hitZone.on('pointerdown', () => {
      if (this.isSpinning) return;
      this.useDirty = !this.useDirty;
      const px = this.toggleContainer!.x;
      const py = this.toggleContainer!.y;
      this.toggleContainer!.destroy();
      this.elements = this.elements.filter(e => e !== this.toggleContainer);
      this.renderToggle(px, py);
      // Refresh chip selector to update grey-out state
      this.refreshChipSelector();
    });

    this.elements.push(this.toggleContainer);
  }

  private renderTournerButton(x: number, y: number): void {
    this.tournerBtn = this.scene.add.container(x, y).setDepth(110);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x7b2fbe, 1);
    bg.fillRoundedRect(-55, -16, 110, 32, 8);
    bg.lineStyle(2, 0xccaaff, 0.7);
    bg.strokeRoundedRect(-55, -16, 110, 32, 8);
    this.tournerBtn.add(bg);

    const txt = this.scene.add.text(0, 0, 'TOURNER', {
      fontSize: '14px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.tournerBtn.add(txt);

    const hitZone = this.scene.add.zone(0, 0, 110, 32)
      .setInteractive({ useHandCursor: true });
    this.tournerBtn.add(hitZone);

    hitZone.on('pointerdown', () => this.spin());
    hitZone.on('pointerover', () => {
      if (!this.isSpinning) this.tournerBtn!.setScale(1.05);
    });
    hitZone.on('pointerout', () => this.tournerBtn!.setScale(1.0));

    this.elements.push(this.tournerBtn);
  }

  private renderClearButton(x: number, y: number): void {
    const container = this.scene.add.container(x, y).setDepth(110);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x331111, 1);
    bg.fillRoundedRect(-40, -14, 80, 28, 8);
    bg.lineStyle(1, 0xff4444, 0.5);
    bg.strokeRoundedRect(-40, -14, 80, 28, 8);
    container.add(bg);

    const txt = this.scene.add.text(0, 0, 'EFFACER', {
      fontSize: '12px',
      fontFamily: 'monospace',
      color: '#ff6666',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(txt);

    const hitZone = this.scene.add.zone(0, 0, 80, 28)
      .setInteractive({ useHandCursor: true });
    container.add(hitZone);

    hitZone.on('pointerdown', () => {
      if (this.isSpinning || this.showingResults) return;
      this.pendingBets = [];
      this.updatePendingBetsDisplay();
      this.renderBetChips();
    });
    hitZone.on('pointerover', () => container.setAlpha(0.8));
    hitZone.on('pointerout', () => container.setAlpha(1));

    this.elements.push(container);
  }

  /* ===================================================================
   *  CHIP PLACEMENT ON TABLE -- corner placement with badge
   * =================================================================== */

  /** Get the cell key for a given bet */
  private getCellKey(type: string, numbers: number[]): string {
    if (type === 'STRAIGHT' && numbers.length === 1) {
      return `num_${numbers[0]}`;
    }
    if (type === 'COLUMN' && numbers.length === 1) {
      return `col_${numbers[0]}`;
    }
    if (type === 'DOZEN' && numbers.length === 1) {
      return `dozen_${numbers[0]}`;
    }
    return `outside_${type}`;
  }

  /** Aggregate pending bets by cell key */
  private aggregateBetsByCell(): Map<string, CellBetInfo> {
    const grouped = new Map<string, CellBetInfo>();

    for (const bet of this.pendingBets) {
      const cellKey = this.getCellKey(bet.type, bet.numbers);
      const existing = grouped.get(cellKey) || { totalAmount: 0, highestDenom: 0 };
      existing.totalAmount += bet.amount;
      if (bet.amount > existing.highestDenom) {
        existing.highestDenom = bet.amount;
      }
      grouped.set(cellKey, existing);
    }

    return grouped;
  }

  /** Draw a chip at position with amount badge (CrapsGame style) */
  private drawBetChip(
    x: number,
    y: number,
    totalAmount: number,
    highestDenom: number,
  ): Phaser.GameObjects.Container {
    const W = this.scene.scale.width;
    const chipR = Math.max(W * 0.008, 6);
    const color = CHIP_COLORS[highestDenom] || 0x888888;
    const container = this.scene.add.container(x, y);

    // Chip body
    const g = this.scene.add.graphics();
    g.fillStyle(color, 1);
    g.fillCircle(0, 0, chipR);
    g.lineStyle(Math.max(1, W * 0.0015), 0xffffff, 0.9);
    g.strokeCircle(0, 0, chipR);
    // Inner ring
    g.lineStyle(Math.max(0.5, W * 0.0008), 0xffffff, 0.35);
    g.strokeCircle(0, 0, chipR * 0.65);
    container.add(g);

    // Denomination text on chip
    const denomText = highestDenom >= 1000 ? `${Math.round(highestDenom / 1000)}K` : `${highestDenom}`;
    const chipLabel = this.scene.add
      .text(0, 0, denomText, {
        fontSize: Math.max(Math.round(chipR * 0.7), 5) + 'px',
        fontFamily: 'monospace',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    container.add(chipLabel);

    // Badge circle on top-right of chip showing total
    const badgeR = Math.max(chipR * 0.55, 4);
    const badgeX = chipR * 0.7;
    const badgeY = -chipR * 0.7;
    const badge = this.scene.add.graphics();
    badge.fillStyle(0x222222, 0.9);
    badge.fillCircle(badgeX, badgeY, badgeR);
    badge.lineStyle(1, GOLD, 0.9);
    badge.strokeCircle(badgeX, badgeY, badgeR);
    container.add(badge);

    const amtStr = totalAmount >= 1000 ? `$${(totalAmount / 1000).toFixed(1)}K` : `$${totalAmount}`;
    const badgeText = this.scene.add
      .text(badgeX, badgeY, amtStr, {
        fontSize: Math.max(Math.round(badgeR * 0.85), 4) + 'px',
        fontFamily: 'monospace',
        color: '#ffd700',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    container.add(badgeText);

    container.setDepth(106);
    this.elements.push(container);
    return container;
  }

  /** Render all bet chips on table -- corner placement, grouped by cell */
  private renderBetChips(): void {
    // Destroy old chip containers
    for (const c of this.chipContainers) {
      try { c.destroy(); } catch { /* ok */ }
    }
    this.chipContainers = [];

    const grouped = this.aggregateBetsByCell();

    for (const [cellKey, info] of grouped) {
      const cellRect = this.tableCells.get(cellKey);
      if (!cellRect) continue;

      const { x, y, w, h } = cellRect;
      const W = this.scene.scale.width;
      const chipR = Math.max(W * 0.008, 6);

      // Bottom-right corner placement
      const chipX = x + w - chipR - w * 0.06;
      const chipY = y + h - chipR - h * 0.08;

      const chip = this.drawBetChip(chipX, chipY, info.totalAmount, info.highestDenom);

      // Pulsing glow for pending bets
      chip.setAlpha(0.85);
      const tw = this.scene.tweens.add({
        targets: chip,
        alpha: 1,
        duration: 400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.InOut',
      });
      this.tweens.push(tw);

      this.chipContainers.push(chip);
    }
  }

  /** Add a bet (multi-click stacking) */
  private placeBet(type: string, numbers: number[], label: string): void {
    if (this.isSpinning || this.showingResults) return;

    console.log(`[ROULETTE] Bet placed: type=${type}, numbers=[${numbers.join(',')}], amount=${this.betAmount}, useDirty=${this.useDirty}, label=${label}`);

    this.pendingBets.push({
      type,
      numbers,
      amount: this.betAmount,
      useDirty: this.useDirty,
      label,
    });

    this.updatePendingBetsDisplay();
    this.renderBetChips();

    // Flash the cell to confirm
    const cellKey = this.getCellKey(type, numbers);
    const cellRect = this.tableCells.get(cellKey);
    if (cellRect) {
      const flash = this.scene.add.graphics().setDepth(105);
      flash.fillStyle(0xffffff, 0.25);
      flash.fillRect(cellRect.x, cellRect.y, cellRect.w, cellRect.h);
      this.elements.push(flash);

      const tw = this.scene.tweens.add({
        targets: flash,
        alpha: 0,
        duration: 250,
        onComplete: () => flash.destroy(),
      });
      this.tweens.push(tw);
    }

    // Refresh chip selector to update grey-out
    this.refreshChipSelector();
  }

  /** Remove a bet (right-click) -- subtracts selected chip denomination from matching cell */
  private removeBet(type: string, numbers: number[], label: string): void {
    if (this.isSpinning || this.showingResults) return;

    const cellKey = this.getCellKey(type, numbers);

    // Find the last pending bet matching this cell key and remove it
    for (let i = this.pendingBets.length - 1; i >= 0; i--) {
      const bet = this.pendingBets[i];
      const betCellKey = this.getCellKey(bet.type, bet.numbers);
      if (betCellKey === cellKey) {
        this.pendingBets.splice(i, 1);
        console.log(`[ROULETTE] Bet removed from ${label}`);
        break;
      }
    }

    this.updatePendingBetsDisplay();
    this.renderBetChips();
    this.refreshChipSelector();
  }

  private updatePendingBetsDisplay(): void {
    if (!this.pendingBetsText) return;

    if (this.pendingBets.length === 0) {
      this.pendingBetsText.setText('Mises: aucune');
      return;
    }

    // Aggregate by label for display
    const grouped = new Map<string, { amount: number; useDirty: boolean }>();
    for (const b of this.pendingBets) {
      const key = `${b.label}_${b.useDirty}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.amount += b.amount;
      } else {
        grouped.set(key, { amount: b.amount, useDirty: b.useDirty });
      }
    }

    const lines: string[] = [];
    for (const [key, info] of grouped) {
      const label = key.split('_')[0];
      const dirtyTag = info.useDirty ? '(sale)' : '(propre)';
      lines.push(`${label} $${info.amount} ${dirtyTag}`);
    }
    this.pendingBetsText.setText('Mises: ' + lines.join(' | '));
  }

  /* ===================================================================
   *  SPIN -- Send bets then spin
   * =================================================================== */

  private spin(): void {
    if (this.isSpinning) return;
    console.log(`[ROULETTE] TOURNER clicked, pendingBets=${this.pendingBets.length}`);
    if (this.pendingBets.length === 0) {
      if (this.resultText) {
        this.resultText.setText('Placez au moins une mise!');
        this.resultText.setColor('#ff8800');
        this.resultText.setAlpha(1);
      }
      return;
    }

    this.isSpinning = true;
    this.showingResults = false;
    if (this.tournerBtn) this.tournerBtn.setAlpha(0.5);

    if (this.resultText) {
      this.resultText.setAlpha(0);
      this.resultText.setText('');
    }

    const bets = this.pendingBets.map(b => ({
      type: b.type,
      numbers: b.numbers,
      amount: b.amount,
      useDirty: b.useDirty,
    }));
    console.log(`[ROULETTE] Pending bets before spin:`, bets);

    socketManager.sendAction('CASINO_PLAY_ROULETTE', {
      buildingId: this.building.id,
      action: 'PLACE_BET',
      bets,
    });

    socketManager.onceActionResult((betResult) => {
      if (!betResult.success) {
        this.isSpinning = false;
        if (this.tournerBtn) this.tournerBtn.setAlpha(1);

        if (this.resultText) {
          this.resultText.setText(betResult.error || 'Erreur de mise');
          this.resultText.setColor('#ff4444');
          this.resultText.setAlpha(1);
        }
        return;
      }

      socketManager.sendAction('CASINO_PLAY_ROULETTE', {
        buildingId: this.building.id,
        action: 'SPIN',
      });

      socketManager.onceActionResult((result) => {
        if (result.success && result.data) {
          const data = result.data as Record<string, any>;
          const rouletteResult = (data.details || data) as RouletteResult;

          if (!rouletteResult || rouletteResult.winningNumber === undefined) {
            console.error('[ROULETTE] Invalid result from server:', data);
            this.isSpinning = false;
            if (this.tournerBtn) this.tournerBtn.setAlpha(1);
            return;
          }

          console.log(`[ROULETTE] Result: winningNumber=${rouletteResult.winningNumber}, color=${rouletteResult.color}`);
          rouletteResult.betResults?.forEach((br: any, i: number) => {
            console.log(`[ROULETTE]   bet[${i}]: type=${br.type}, outcome=${br.outcome}, payout=${br.payout}`);
          });

          if (data.newClean !== undefined) this.balanceClean = data.newClean;
          if (data.newDirty !== undefined) this.balanceDirty = data.newDirty;

          this.animateWheel(rouletteResult.winningNumber, rouletteResult);
        } else {
          this.isSpinning = false;
          if (this.tournerBtn) this.tournerBtn.setAlpha(1);

          if (this.resultText) {
            this.resultText.setText(result.error || 'Erreur');
            this.resultText.setColor('#ff4444');
            this.resultText.setAlpha(1);
          }
        }
      });
    });
  }

  /* ===================================================================
   *  WHEEL ANIMATION -- Spin with ball orbit
   * =================================================================== */

  private animateWheel(winningNumber: number, rouletteResult: RouletteResult): void {
    if (!this.wheelContainer) return;

    const wheelCenterX = this.wheelCenterX;
    const wheelCenterY = this.wheelCenterY;
    const radius = this.wheelRadius;

    // Show ball
    if (this.ballGraphic) {
      this.ballGraphic.setAlpha(1);
    }

    // Find the angle for the winning number
    const winIndex = WHEEL_ORDER.indexOf(winningNumber);
    const segmentAngle = 360 / WHEEL_ORDER.length;
    const targetAngle = -(winIndex * segmentAngle);
    const totalAngle = 360 * 5 + targetAngle;

    // Spin the wheel
    const tw1 = this.scene.tweens.add({
      targets: this.wheelContainer,
      angle: totalAngle,
      duration: 3500,
      ease: 'Cubic.Out',
    });
    this.tweens.push(tw1);

    // Animate ball orbiting in opposite direction, then settling
    const ballOrbitRadius = radius - Math.max(6, radius * 0.1);
    const ballR = Math.max(3, radius * 0.06);
    let ballAngle = 0;
    const ballSpeed = 0.15;
    let elapsed = 0;
    const totalDuration = 3500;

    const ballTimer = this.scene.time.addEvent({
      delay: 16,
      callback: () => {
        elapsed += 16;
        const progress = elapsed / totalDuration;

        // Ball slows down as progress increases
        const currentSpeed = ballSpeed * (1 - progress * 0.8);
        ballAngle += currentSpeed;

        // Ball gets closer to wheel as it slows
        const currentRadius = ballOrbitRadius * (1 - progress * 0.15);

        const bx = wheelCenterX + Math.cos(ballAngle) * currentRadius;
        const by = wheelCenterY + Math.sin(ballAngle) * currentRadius;

        if (this.ballGraphic) {
          this.ballGraphic.clear();
          this.ballGraphic.fillStyle(0xffffff, 1);
          this.ballGraphic.fillCircle(bx, by, ballR);
          this.ballGraphic.lineStyle(1, 0xcccccc, 0.8);
          this.ballGraphic.strokeCircle(bx, by, ballR);
        }

        if (elapsed >= totalDuration) {
          ballTimer.destroy();
          this.timers = this.timers.filter(t => t !== ballTimer);

          // Place ball on winning number position
          const winAngleRad = ((winIndex * (360 / WHEEL_ORDER.length)) - 90) * Math.PI / 180;
          const finalR = ballOrbitRadius * 0.85;
          const finalBx = wheelCenterX + Math.cos(winAngleRad) * finalR;
          const finalBy = wheelCenterY + Math.sin(winAngleRad) * finalR;

          if (this.ballGraphic) {
            this.ballGraphic.clear();
            this.ballGraphic.fillStyle(0xffffff, 1);
            this.ballGraphic.fillCircle(finalBx, finalBy, ballR);
            this.ballGraphic.lineStyle(1, 0xcccccc, 0.8);
            this.ballGraphic.strokeCircle(finalBx, finalBy, ballR);
          }

          this.showResults(winningNumber, rouletteResult);
        }
      },
      loop: true,
    });
    this.timers.push(ballTimer);
  }

  /* ===================================================================
   *  RESULTS -- Flash winning bets, show payouts
   * =================================================================== */

  private showResults(winningNumber: number, rouletteResult: RouletteResult): void {
    this.showingResults = true;
    const { width: W, height: H } = this.scene.scale;

    // Show winning number
    let numColor = '#00aa00';
    if (RED_NUMBERS.includes(winningNumber)) numColor = '#ff4444';
    else if (BLACK_NUMBERS.includes(winningNumber)) numColor = '#ffffff';

    if (this.winningMarker) {
      this.winningMarker.setText(`${winningNumber}`);
      this.winningMarker.setColor(numColor);
      this.winningMarker.setScale(0.5);

      const tw = this.scene.tweens.add({
        targets: this.winningMarker,
        scaleX: 1.3,
        scaleY: 1.3,
        duration: 300,
        ease: 'Back.Out',
        onComplete: () => {
          this.scene.tweens.add({
            targets: this.winningMarker,
            scaleX: 1,
            scaleY: 1,
            duration: 200,
          });
        },
      });
      this.tweens.push(tw);
    }

    // Flash winning number cell on table
    const winCell = this.tableCells.get(`num_${winningNumber}`);
    if (winCell) {
      let flashCount = 0;
      const flashTimer = this.scene.time.addEvent({
        delay: 200,
        callback: () => {
          flashCount++;
          const flashG = this.scene.add.graphics().setDepth(105);

          if (flashCount % 2 === 1) {
            flashG.fillStyle(0x00ff00, 0.5);
          } else {
            flashG.fillStyle(0x00ff00, 0.1);
          }
          flashG.fillRect(winCell.x, winCell.y, winCell.w, winCell.h);
          this.elements.push(flashG);

          const tw = this.scene.tweens.add({
            targets: flashG,
            alpha: 0,
            duration: 180,
            onComplete: () => flashG.destroy(),
          });
          this.tweens.push(tw);

          if (flashCount >= 8) {
            flashTimer.destroy();
            this.timers = this.timers.filter(t => t !== flashTimer);

            // Keep winning cell highlighted
            const highlightG = this.scene.add.graphics().setDepth(105);
            highlightG.fillStyle(0x00cc00, 0.4);
            highlightG.fillRect(winCell.x, winCell.y, winCell.w, winCell.h);
            highlightG.lineStyle(2, 0x00ff00, 0.8);
            highlightG.strokeRect(winCell.x, winCell.y, winCell.w, winCell.h);
            this.elements.push(highlightG);
          }
        },
        loop: true,
      });
      this.timers.push(flashTimer);
    }

    // Update balance
    this.balanceText?.setText(this.getBalanceLabel());

    // Process bet results -- show floating +$X for wins, red flash for losses
    const betResults = rouletteResult.betResults || [];
    let totalWin = 0;

    betResults.forEach((br, idx) => {
      if (br.outcome === 'WIN') {
        totalWin += br.payout;

        // Floating win text from the bet position
        const cx = W / 2;
        const floatX = cx + (idx - betResults.length / 2) * Math.min(60, W * 0.06);
        const floatY = H * 0.7;

        const winTxt = this.scene.add.text(floatX, floatY, `+$${br.payout}`, {
          fontSize: '14px',
          fontFamily: 'monospace',
          color: '#00ff00',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 2,
        }).setOrigin(0.5).setDepth(110);
        this.elements.push(winTxt);

        const tw = this.scene.tweens.add({
          targets: winTxt,
          y: floatY - H * 0.05,
          alpha: 0,
          duration: 1500,
          delay: 300,
          ease: 'Sine.Out',
        });
        this.tweens.push(tw);
      }
    });

    // Show result text
    if (this.resultText) {
      if (totalWin > 0) {
        this.resultText.setText(`+$${totalWin}!`);
        this.resultText.setColor('#e6b800');
        this.resultText.setAlpha(1);
        this.resultText.setScale(0.3);

        const tw = this.scene.tweens.add({
          targets: this.resultText,
          scaleX: 1.3,
          scaleY: 1.3,
          duration: 400,
          ease: 'Back.Out',
          onComplete: () => {
            this.scene.tweens.add({
              targets: this.resultText,
              scaleX: 1,
              scaleY: 1,
              duration: 200,
              ease: 'Sine.Out',
            });
          },
        });
        this.tweens.push(tw);
      } else {
        this.resultText.setText('Perdu!');
        this.resultText.setColor('#ff4444');
        this.resultText.setAlpha(1);
      }
    }

    // Clear pending bets and visuals
    this.pendingBets = [];
    this.updatePendingBetsDisplay();

    // Remove placed chip visuals with fade
    for (const cc of this.chipContainers) {
      const tw = this.scene.tweens.add({
        targets: cc,
        alpha: 0,
        duration: 800,
        delay: 500,
        onComplete: () => cc.destroy(),
      });
      this.tweens.push(tw);
    }
    this.chipContainers = [];

    // Show post-game buttons after delay
    const btnTimer = this.scene.time.addEvent({
      delay: 1500,
      callback: () => {
        this.timers = this.timers.filter(t => t !== btnTimer);
        this.showPostGameButtons();
      },
    });
    this.timers.push(btnTimer);
  }

  private showPostGameButtons(): void {
    const { width: W, height: H } = this.scene.scale;
    const cx = W / 2;
    const btnY = H * 0.9;

    this.isSpinning = false;

    // REJOUER button
    const rejouerContainer = this.scene.add.container(cx - W * 0.06, btnY).setDepth(103);
    const rejouerBg = this.scene.add.graphics();
    rejouerBg.fillStyle(0x7b2fbe, 1);
    rejouerBg.fillRoundedRect(-50, -14, 100, 28, 8);
    rejouerBg.lineStyle(1, 0xccaaff, 0.5);
    rejouerBg.strokeRoundedRect(-50, -14, 100, 28, 8);
    rejouerContainer.add(rejouerBg);
    const rejouerTxt = this.scene.add.text(0, 0, 'REJOUER', {
      fontSize: '13px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    rejouerContainer.add(rejouerTxt);
    const rejouerHit = this.scene.add.zone(0, 0, 100, 28).setInteractive({ useHandCursor: true });
    rejouerContainer.add(rejouerHit);

    rejouerHit.on('pointerdown', () => {
      this.showingResults = false;
      if (this.wheelContainer) this.wheelContainer.setAngle(0);
      if (this.winningMarker) this.winningMarker.setText('');
      if (this.ballGraphic) this.ballGraphic.setAlpha(0);
      if (this.resultText) {
        this.resultText.setAlpha(0);
        this.resultText.setText('');
      }
      if (this.tournerBtn) this.tournerBtn.setAlpha(1);
      rejouerContainer.destroy();
      retourContainer.destroy();
      this.elements = this.elements.filter(e => e !== rejouerContainer && e !== retourContainer);
      // Refresh chip selector to update greyed-out chips
      this.refreshChipSelector();
    });
    rejouerHit.on('pointerover', () => rejouerContainer.setScale(1.05));
    rejouerHit.on('pointerout', () => rejouerContainer.setScale(1.0));
    this.elements.push(rejouerContainer);

    // RETOUR button
    const retourContainer = this.scene.add.container(cx + W * 0.06, btnY).setDepth(103);
    const retourBg = this.scene.add.graphics();
    retourBg.fillStyle(0x333333, 1);
    retourBg.fillRoundedRect(-50, -14, 100, 28, 8);
    retourContainer.add(retourBg);
    const retourTxt = this.scene.add.text(0, 0, 'RETOUR', {
      fontSize: '13px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    retourContainer.add(retourTxt);
    const retourHit = this.scene.add.zone(0, 0, 100, 28).setInteractive({ useHandCursor: true });
    retourContainer.add(retourHit);

    retourHit.on('pointerdown', () => this.onBack());
    retourHit.on('pointerover', () => retourContainer.setAlpha(0.8));
    retourHit.on('pointerout', () => retourContainer.setAlpha(1));
    this.elements.push(retourContainer);
  }

  /* ===================================================================
   *  HELPERS
   * =================================================================== */

  private getToggleLabel(): string {
    return this.useDirty ? 'Sale' : 'Propre';
  }

  private getBalanceLabel(): string {
    return `Propre: $${Math.floor(this.balanceClean)}  |  Sale: $${Math.floor(this.balanceDirty)}`;
  }

  destroy(): void {
    this.timers.forEach(t => { try { t.destroy(); } catch { /* ok */ } });
    this.timers = [];

    this.tweens.forEach(t => { try { t.stop(); } catch { /* ok */ } });
    this.tweens = [];

    this.chipSelectors.forEach(c => { try { c.destroy(); } catch { /* ok */ } });
    this.chipSelectors = [];

    this.chipContainers.forEach(c => { try { c.destroy(); } catch { /* ok */ } });
    this.chipContainers = [];

    this.elements.forEach(e => { try { e.destroy(); } catch { /* ok */ } });
    this.elements = [];

    this.tableCells.clear();
    this.balanceText = null;
    this.pendingBetsText = null;
    this.resultText = null;
    this.wheelContainer = null;
    this.ballGraphic = null;
    this.winningMarker = null;
    this.tournerBtn = null;
    this.toggleContainer = null;
  }
}
